// Imports
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import FieldErrorMessage from '../../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import Input from '../../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Phone from '../../../../../../../shareables/foundation/front-end/components/forms/fields/phone';
import Select from '../../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import MultiFieldContainer from '../../../../../../../shareables/foundation/front-end/components/forms/multi-field-container';
import theme from '../../../../../../../shareables/theme';
import { GetGeneralResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/general/get';
import { GetRegistrationResponse } from '../../../../../../../shareables/types/api/main/website/settings/registration/get';
import AskGenderModal from '../ask-gender-modal';
import AskGenderModalNote from '../ask-gender-modal/ask-gender-modal-note';


// Styled components
const FirstNameError = styled.div`
	margin-top: -1.25rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		display: none;
	}
`;


// Define the accepted props
interface Props {
	generalSettings?: GetGeneralResponse;
	askGender: boolean;
	accountTypes?: GetRegistrationResponse['accountTypes'];
	handleAccountTypeChange?: (accountID: number) => void;
	password?: boolean;
	disabled?: boolean;
}


// Function component
const RegistrationFields: React.FC<Props> = ({
	generalSettings,
	askGender,
	accountTypes,
	handleAccountTypeChange,
	password,
	disabled,
}) => {
	// Use state
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	
	
	// Handle first name
	const handleFirstName = (firstName: string) => {
		if (firstName.includes('&') || firstName.includes('/') || firstName.includes('\\') || firstName.includes(' and ')) {
			setErrorMessage('Need to add a second person? Use the “Add member” button below instead.');
		} else {
			setErrorMessage(undefined);
		}
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			{accountTypes && (
				<Select
					name='accountTypeID'
					label='I am a…'
					placeholder='Please select…'
					onChange={(id) => handleAccountTypeChange?.(Number(id))}
					required
				>
					{accountTypes.map((accountType) => (
						<option value={accountType.id} key={accountType.id}>
							{accountType.name}
						</option>
					))}
				</Select>
			)}
			<MultiFieldContainer growAll>
				<Input
					initialValue={generalSettings?.firstName}
					type='text'
					label='First name'
					name='firstName'
					placeholder='John'
					disabled={disabled}
					maxLength={50}
					onBlur={(event) => handleFirstName(event.target.value)}
					errorMessage={generalSettings ? undefined : errorMessage}
					required
				/>
				<Input
					initialValue={generalSettings?.lastName}
					type='text'
					label='Last name'
					name='lastName'
					placeholder='Doe'
					disabled={disabled}
					maxLength={50}
					required
				/>
			</MultiFieldContainer>
			{errorMessage && !generalSettings && (
				<FirstNameError>
					<FieldErrorMessage message={errorMessage} />
				</FirstNameError>
			)}
			<Input
				initialValue={generalSettings?.email}
				type='email'
				label='Email address'
				name='email'
				placeholder='you@example.com'
				disabled={disabled}
				maxLength={75}
				required
			/>
			<Phone
				initialValue={generalSettings?.cellPhone ?? undefined}
				label='Cell phone'
				name={generalSettings ? 'cellPhone' : 'phone'}
				disabled={disabled}
				maxLength={20}
			/>
			{password && (
				<Input
					name='password'
					type='password'
					label={generalSettings ? 'New password' : 'Password'}
					note='(Case sensitive)'
					placeholder='••••••••••'
					data-private
					showStrength
					required
				/>
			)}
			{askGender && (
				<Select
					initialValue={generalSettings?.gender ?? undefined}
					label='Gender'
					name='gender'
					placeholder='Please select…'
					note={<AskGenderModalNote />}
				>
					<option value='M'>Male</option>
					<option value='F'>Female</option>
					<option value='NS'>Prefer not to say</option>
				</Select>
			)}
			
			<AskGenderModal />
		</React.Fragment>
	);
};

export default RegistrationFields;
